import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Copy, Check, Trash2, CornerDownLeft, CheckCircle2, X, Loader2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import BotonIluminar from '../ui/BotonIluminar';
import EstadoVacio from '../ui/EstadoVacio';
import ModalConfirmar from '../ui/ModalConfirmar';
import { useAuth } from '../lib/contextoAuth';
import { modulosVisibles } from '../ui/modulos';

// A dónde se va a ver el resultado de cada acción. Que el enlace exista
// no basta: tiene que llevar a un módulo que ESTA persona tenga. En una
// cuenta de empresa no hay lista de compras —lo que falta va al flujo de
// compras— así que ofrecer «Ver mi lista» ahí manda a una pantalla que no
// está en su menú.
const DONDE_SE_VE = {
  apuntar_en_lista: 'lista-compras',
  marcar_comprado: 'lista-compras',
  marcar_aviso_leido: 'alertas',
};

// Pregúntale a TEGO como le hablarías a un compañero.
//
// POR QUÉ ESTA PANTALLA Y NO SOLO LA BÚSQUEDA. La búsqueda devuelve una
// lista de resultados: sirve para *llegar* a una ficha. Quien está en el
// pasillo con el teléfono en una mano no quiere una lista, quiere la
// respuesta — dónde está, cuántas quedan, y si puede llevárselas.
//
// LAS SUGERENCIAS NO SON ADORNO. Una caja de texto vacía no dice qué se
// le puede preguntar, y quien no lo sabe no pregunta: prueba una vez, no
// acierta, y no vuelve. Los chips enseñan el repertorio sin manual.

// Negritas de la respuesta, sin meter una biblioteca de Markdown por
// tres asteriscos. Todo lo demás se pinta como texto: el contenido lo
// compone el servidor, pero aun así aquí no se interpreta HTML.
function Texto({ children }) {
  const partes = String(children).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {partes.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) {
          return <strong key={i} className="text-gray-900">{p.slice(2, -2)}</strong>;
        }
        if (p.startsWith('`') && p.endsWith('`')) {
          return (
            <code key={i} className="font-mono text-xs bg-gray-100 px-1 rounded">
              {p.slice(1, -1)}
            </code>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

// La pregunta que decide si algo pasa o no pasa.
//
// TRES REGLAS DE ESTA PIEZA, y las tres son de usuario cero:
//
//  1. LAS DOS SALIDAS PESAN IGUAL DE POCO. «No» no está escondido ni en
//     gris: decidir que no es una respuesta tan válida como decidir que
//     sí, y esconderla empuja a aceptar por inercia.
//  2. CONFIRMA QUE OCURRIÓ. Al terminar no desaparece el bloque dejando la
//     duda: dice «Hecho» y ofrece ir a verlo. Una acción sin confirmación
//     visible es una acción que la gente repite «por si acaso».
//  3. SI FALLA, DICE QUÉ HACER. El mensaje del servidor ya está escrito
//     para leerse; aquí no se sustituye por un «error» genérico.
function Propuesta({ estado, onSi, onNo, irA, destino }) {
  if (estado === 'hecha') {
    return (
      <div className="flex flex-wrap items-center gap-2 pt-1 text-sm text-green-800">
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="font-semibold">Hecho.</span>
        {/* SOLO SI ESTA PERSONA TIENE ESE MODULO. Un enlace que lleva a una
            pantalla que no está en tu menú es peor que no ofrecerlo: se
            toca, no pasa nada, y a partir de ahí no se vuelve a tocar
            ninguno. Quien decide es la misma función que arma el menú, no
            una copia de su regla aquí. */}
        {destino && (
          <button
            type="button"
            onClick={() => irA(destino.modulo, {})}
            className="text-xs font-semibold text-[var(--color-marca)] underline"
          >
            {destino.etiqueta}
          </button>
        )}
      </div>
    );
  }
  if (estado === 'descartada') {
    return (
      <div className="pt-1 text-sm text-gray-500">
        No hice nada. Si lo quieres, vuelve a pedírmelo.
      </div>
    );
  }
  if (estado && estado !== 'haciendo') {
    return (
      <div className="pt-1 space-y-2">
        <div className="text-sm text-red-800 border border-red-200 bg-red-50
                        rounded-lg p-2">{estado}</div>
        <button type="button" onClick={onSi}
                className="text-xs font-semibold text-[var(--color-marca)] underline">
          Volver a intentarlo
        </button>
      </div>
    );
  }

  const haciendo = estado === 'haciendo';
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      <button
        type="button"
        onClick={onSi}
        disabled={haciendo}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white
                   bg-[var(--color-marca)] px-4 py-2.5 rounded-xl
                   disabled:opacity-60 focus:outline-none focus:ring-2
                   focus:ring-[var(--color-marca)]"
      >
        {haciendo
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <Check className="h-4 w-4" aria-hidden="true" />}
        {haciendo ? 'Haciéndolo…' : 'Sí, hazlo'}
      </button>
      <button
        type="button"
        onClick={onNo}
        disabled={haciendo}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700
                   border border-gray-300 px-4 py-2.5 rounded-xl
                   disabled:opacity-60 focus:outline-none focus:ring-2
                   focus:ring-[var(--color-marca)]"
      >
        <X className="h-4 w-4" aria-hidden="true" /> No
      </button>
    </div>
  );
}


function Respuesta({ texto }) {
  return (
    <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
      <Texto>{texto}</Texto>
    </div>
  );
}

export default function Asistente({ irA }) {
  const { tieneNivel, esEmpresa, puede } = useAuth();
  const [historial, setHistorial] = useState([]);
  const [pregunta, setPregunta] = useState('');
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState(null);
  // La última pregunta que falló, para «Volver a intentarlo» (EXPERIENCIA
  // E2): un error que solo describe deja a la persona recargando la
  // página a ver si suena la flauta.
  const [ultimaFallida, setUltimaFallida] = useState(null);
  const [copiado, setCopiado] = useState(null);
  const [borrar, setBorrar] = useState(false);
  // QUÉ PASÓ CON CADA PROPUESTA, por mensaje. El servidor no lo sabe: para
  // él, proponer y ejecutar son dos peticiones sin memoria entre ellas —a
  // propósito—, así que quién dijo que sí vive aquí mientras dure la
  // conversación. Recargar limpia esto y la propuesta caduca sola, que es
  // el comportamiento correcto: nadie debería confirmar mañana algo que
  // pidió hoy.
  const [resueltas, setResueltas] = useState({});
  const [sugerencias, setSugerencias] = useState([
    '¿dónde están las balatas?',
    '¿qué se movió hoy?',
    '¿qué está esperando autorización?',
    '¿qué puedes hacer?',
  ]);
  const fondo = useRef(null);

  useEffect(() => {
    let vivo = true;
    api.get('/bot/historial?limite=20')
      .then((h) => vivo && setHistorial(h.slice().reverse()))
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  // Al llegar una respuesta, la conversación baja sola: si no, la
  // respuesta aparece fuera de la pantalla y parece que no pasó nada.
  useEffect(() => {
    fondo.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [historial, pensando]);

  const enviar = useCallback(async (texto) => {
    const q = (texto ?? pregunta).trim();
    if (!q || pensando) return;
    setPregunta('');
    setError(null);
    setPensando(true);
    try {
      const r = await api.post('/bot/preguntar', { pregunta: q });
      setHistorial((h) => [...h, r]);
      setUltimaFallida(null);
      if (r.sugerencias?.length) setSugerencias(r.sugerencias);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo preguntar.');
      setUltimaFallida(q);
    } finally {
      setPensando(false);
    }
  }, [pregunta, pensando]);

  async function copiar(texto, id) {
    try {
      await navigator.clipboard.writeText(texto.replace(/\*\*|`/g, ''));
      setCopiado(id);
      setTimeout(() => setCopiado(null), 1500);
    } catch { /* sin portapapeles: no es un fallo que valga la pena contar */ }
  }

  // Los módulos que ESTA persona tiene, preguntados a la misma función
  // que arma la barra lateral. Reimplementar aquí la regla de quién ve
  // qué acabaría con las dos versiones divergiendo el día que cambie una.
  const visibles = modulosVisibles(tieneNivel, esEmpresa, puede).flatMap((g) => g.items);

  function destinoDe(propuesta) {
    const id = DONDE_SE_VE[propuesta?.accion];
    const modulo = visibles.find((m) => m.id === id);
    return modulo ? { modulo: modulo.id, etiqueta: `Ver ${modulo.etiqueta.toLowerCase()}` } : null;
  }

  // Confirmar es una SEGUNDA petición, la única que escribe. Se manda lo
  // que el servidor propuso, y él lo revalida entero: que la propuesta
  // saliera de nosotros no exime de comprobar permisos ni argumentos.
  async function confirmar(m) {
    setResueltas((r) => ({ ...r, [m.id]: 'haciendo' }));
    try {
      await api.post('/bot/ejecutar', {
        accion: m.propuesta.accion,
        argumentos: m.propuesta.argumentos,
      });
      setResueltas((r) => ({ ...r, [m.id]: 'hecha' }));
    } catch (e) {
      setResueltas((r) => ({
        ...r,
        [m.id]: e instanceof ErrorApi ? e.message : 'No se pudo hacer. Inténtalo otra vez.',
      }));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Asistente</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Pregúntale a TEGO dónde está algo, cuánto queda o qué se movió.
            Escribe como hablas: no hace falta ortografía ni acentos.
          </p>
        </div>
        {historial.length > 0 && (
          <button type="button" onClick={() => setBorrar(true)}
                  className="inline-flex items-center gap-1.5 border border-gray-300
                             text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg
                             hover:bg-gray-50 focus:outline-none focus:ring-2
                             focus:ring-[var(--color-marca)]">
            <Trash2 className="h-4 w-4" aria-hidden="true" /> Borrar conversación
          </button>
        )}
      </div>

      {historial.length === 0 && !pensando && (
        <EstadoVacio
          icono={Sparkles}
          titulo="Pregunta lo que necesites"
          descripcion="Te respondo con la ubicación exacta, lo que queda y lo que está apartado. Toca una de las preguntas de abajo para empezar."
        />
      )}

      <div className="space-y-3">
        {historial.map((m) => (
          <div key={m.id} className="space-y-2">
            {/* Lo que preguntó, a la derecha: la conversación se lee de un
                vistazo sin buscar quién dijo qué. */}
            <div className="flex justify-end">
              <div className="bg-[var(--color-marca)] text-white text-sm rounded-2xl
                              rounded-br-sm px-3 py-2 max-w-[85%]">
                {m.pregunta}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm p-3
                            max-w-[95%] space-y-2">
              <Respuesta texto={m.texto ?? m.respuesta} />

              {/* EL AVISO DE MODO REDUCIDO (M-3).
                  DISCRETO Y HONESTO, que es una combinación difícil: si se
                  calla, alguien se pelea diez minutos con una frase que hoy
                  no se entiende y culpa al sistema entero; si grita, se
                  lee como una avería y deja de usarse.
                  Va en gris, pequeño y debajo de la respuesta — nunca en
                  ámbar ni en rojo: no hay nada roto, solo se entienden
                  menos formas de preguntar. Y el servidor solo lo manda
                  cuando HABÍA algo mejor: una instalación sin modelo no
                  está degradada, está completa. */}
              {m.aviso_nivel && (
                <p className="text-xs text-gray-500 border-t border-gray-100 pt-1.5">
                  {m.aviso_nivel}
                </p>
              )}

              {/* B6 · LA MARCA DE HONESTIDAD. Con qué se armó la respuesta,
                  en palabras humanas y sin jerga: «Respuesta con IA» o
                  «Respuesta rápida». Pequeña y en gris; no es una avería. */}
              {m.marca && (
                <p className="text-[11px] font-medium text-gray-400">{m.marca}</p>
              )}

              {/* LA CONFIRMACIÓN DE UNA ACCIÓN (M-5).
                  El asistente NO ha hecho nada todavía: propuso. Hasta que
                  alguien toque «Sí», en la base no ha cambiado ni una fila.
                  Por eso esto no es un aviso, es una pregunta con dos
                  salidas — y la de no hacer nada es tan fácil como la otra. */}
              {m.propuesta && (
                <Propuesta
                  estado={resueltas[m.id]}
                  onSi={() => confirmar(m)}
                  onNo={() => setResueltas((r) => ({ ...r, [m.id]: 'descartada' }))}
                  irA={irA}
                  destino={destinoDe(m.propuesta)}
                />
              )}

              {/* Y SI HAY LUZ, LA ENCIENDE. El asistente dice dónde está;
                  esto lo alumbra sin caminar contando charolas. Los botones
                  solo salen para las ubicaciones que tienen una tira
                  instalada y respondiendo — si no hay ninguna, aquí no
                  aparece nada y la respuesta se lee igual que siempre. */}
              {m.ubicaciones?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {m.ubicaciones.map((u) => (
                    <BotonIluminar key={u} ubicacion={u} compacto />
                  ))}
                </div>
              )}

              {/* CADA RESPUESTA LLEVA A DONDE SE RESUELVE. Una respuesta
                  que obliga a ir a buscar otra vez lo que el bot acaba de
                  encontrar deja el trabajo a medias. */}
              {m.acciones?.length > 0 && !(m.propuesta && !resueltas[m.id]) && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {m.acciones.map((a) => (
                    <button
                      key={`${a.modulo}:${a.etiqueta}`}
                      type="button"
                      onClick={() => irA(a.modulo, a.contexto || {})}
                      className="text-xs font-semibold border border-gray-300
                                 text-gray-700 px-2.5 py-1.5 rounded-lg
                                 hover:bg-gray-50 focus:outline-none focus:ring-2
                                 focus:ring-[var(--color-marca)]"
                    >
                      {a.etiqueta}
                    </button>
                  ))}
                </div>
              )}

              <button type="button" onClick={() => copiar(m.texto ?? m.respuesta, m.id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold
                                 text-gray-500 hover:text-gray-800">
                {copiado === m.id
                  ? <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Copiado</>
                  : <><Copy className="h-3.5 w-3.5" aria-hidden="true" /> Copiar</>}
              </button>
            </div>
          </div>
        ))}

        {pensando && (
          <p role="status" className="text-sm text-gray-500">Buscando…</p>
        )}
        <div ref={fondo} />
      </div>

      {error && (
        <div role="alert" className="text-sm text-red-800 bg-red-50 border
                                     border-red-200 rounded-lg p-3">
          <strong className="block">No se pudo preguntar</strong>
          <p className="mt-1">{error} Tu pregunta sigue aquí; no se ha perdido nada.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {ultimaFallida && (
              <button type="button" onClick={() => enviar(ultimaFallida)} disabled={pensando}
                className="rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-800 disabled:opacity-60">
                Volver a intentarlo
              </button>
            )}
            <button type="button" onClick={() => { setError(null); setUltimaFallida(null); }}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700">
              Escribir otra cosa
            </button>
          </div>
        </div>
      )}

      {/* Los chips van ARRIBA del campo: en un teléfono, lo que está
          pegado al teclado es lo que se toca sin pensar. */}
      <div className="flex flex-wrap gap-1.5">
        {sugerencias.map((s) => (
          <button key={s} type="button" onClick={() => enviar(s)}
                  className="text-xs border border-gray-300 text-gray-600 bg-white
                             px-2.5 py-1.5 rounded-full hover:bg-gray-50
                             focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]">
            {s}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); enviar(); }}
            className="sticky bottom-0 bg-[var(--color-lienzo)] pt-2 flex gap-2">
        <input
          value={pregunta}
          onChange={(e) => setPregunta(e.target.value)}
          placeholder="¿dónde están las balatas?"
          aria-label="Escribe tu pregunta"
          className="entrada flex-1"
          autoComplete="off"
        />
        <button type="submit" disabled={!pregunta.trim() || pensando}
                className="inline-flex items-center gap-1.5 bg-[var(--color-marca)]
                           hover:bg-[var(--color-marca-fuerte)] text-white font-semibold
                           px-4 rounded-lg disabled:opacity-50
                           focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]
                           focus:ring-offset-2">
          <Send className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Preguntar</span>
        </button>
      </form>
      <p className="text-xs text-gray-500 flex items-center gap-1">
        <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
        Funciona sin internet: pregunta lo que sea aunque el almacén no tenga señal.
      </p>

      {borrar && (
        <ModalConfirmar
          titulo="Borrar tu conversación"
          descripcion="Se borra solo lo que ves aquí, y solo lo tuyo. El registro de auditoría no se toca: una cosa es limpiar tu pantalla y otra borrar lo que se buscó."
          etiquetaConfirmar="Borrar"
          peligro
          onConfirmar={async () => {
            await api.del('/bot/historial');
            setHistorial([]);
            setBorrar(false);
          }}
          onCerrar={() => setBorrar(false)}
        />
      )}
    </div>
  );
}
